import React from 'react';
import WorkspaceHeader from './WorkspaceHeader';
import LeftTreePanel from './LeftTreePanel';
import RightTableSection from './RightTableSection';
import AddMappingModal from './AddMappingModal';
import { Check, Pencil, Info } from 'lucide-react';
import { DeleteWarningModal } from '../../components/DeleteWarningModal';
import { loadConfigureState, saveConfigureState, mergeConfigureState, resetConfigureStateToDemo } from './statePersistence';
import { buildConfigureIndexes, upsertMappings } from './configureIndexes';
import kpiRegistryData from './newkpiRegistryData.json';
import dummyData from './newdummyData.json';

class DomainMappingWorkspace extends React.Component {
    constructor(props) {
        super(props);
        this.rightPanelRef = React.createRef();
        this.infoPopupRef = React.createRef();
        this.infoIconRef = React.createRef();
        this.resizeRaf = null;
        this.state = {
            businesses: [],
            functions: [],
            kpis: [],
            models: [],
            assets: [],
            mappings: [],

            searchVal: '',
            selectedNode: { type: null, id: null },
            modalOpen: false,
            editingMapping: null,

            showFiltersPanel: false,
            filterBusinesses: new Set(),
            filterFunctionId: 'all',
            filterStatus: 'all',

            showDeleteModal: false,
            deleteMappingIds: [],
            rightHeight: null,
            showDetailedMetrics: false
        };
    }

    getIndexes = (state = this.state) => buildConfigureIndexes(state);

    persistState = (partial, extraLocalState = {}) => {
        this.setState(prevState => {
            const next = mergeConfigureState(prevState, partial);
            saveConfigureState(next);
            return {
                businesses: next.businesses,
                functions: next.functions,
                kpis: next.kpis,
                models: next.models,
                assets: next.assets,
                mappings: next.mappings,
                ...extraLocalState
            };
        });
    };

    handleStateUpdate = (e) => {
        const { businesses, functions, kpis, models, assets, mappings } = e.detail;
        this.setState({ businesses, functions, kpis, models, assets, mappings });
    };

    componentDidMount() {
        let state = loadConfigureState();
        const expectedKpiCount = (kpiRegistryData.kpis || []).length;
        const expectedMappingCount = (dummyData.mappings || []).length;
        if (
            (state.kpis || []).length !== expectedKpiCount
            || (state.mappings || []).length !== expectedMappingCount
        ) {
            state = resetConfigureStateToDemo();
        }
        this.setState({
            businesses: state.businesses,
            functions: state.functions,
            kpis: state.kpis,
            models: state.models,
            assets: state.assets,
            mappings: state.mappings
        });

        window.addEventListener('qualdo-state-update', this.handleStateUpdate);
        document.addEventListener('mousedown', this.handleClickOutside);

        this.resizeObserver = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (!entry) return;
            const height = entry.target.offsetHeight;
            if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
            this.resizeRaf = requestAnimationFrame(() => {
                if (this.state.rightHeight !== height) {
                    this.setState({ rightHeight: height });
                }
            });
        });
        if (this.rightPanelRef.current) {
            this.resizeObserver.observe(this.rightPanelRef.current);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('qualdo-state-update', this.handleStateUpdate);
        document.removeEventListener('mousedown', this.handleClickOutside);
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
    }

    handleClickOutside = (event) => {
        if (
            this.state.showDetailedMetrics &&
            this.infoPopupRef.current &&
            !this.infoPopupRef.current.contains(event.target) &&
            this.infoIconRef.current &&
            !this.infoIconRef.current.contains(event.target)
        ) {
            this.setState({ showDetailedMetrics: false });
        }
    };

    getMetrics = () => {
        const indexes = this.getIndexes();
        const activeKpis = this.state.kpis.filter(indexes.isKpiEff);
        const totalActiveKpis = activeKpis.length;

        if (totalActiveKpis === 0) {
            return {
                mappedPercent: 0,
                autoCount: 0,
                manualCount: 0,
                totalActiveKpis: 0,
                mappedCount: 0,
                unmappedCount: 0,
                totalMappings: 0
            };
        }

        const enabledKpiIds = new Set(
            this.state.mappings.filter(m => m.enabled && m.kpiId).map(m => m.kpiId)
        );
        let mappedCount = 0;
        activeKpis.forEach(k => {
            if (enabledKpiIds.has(k.id)) mappedCount++;
        });

        const unmappedCount = totalActiveKpis - mappedCount;
        const mappedPercent = Math.round((mappedCount / totalActiveKpis) * 100);
        const autoCount = this.state.mappings.filter(m => m.enabled && m.status === 'auto-mapped').length;
        const manualCount = this.state.mappings.filter(m => m.enabled && m.status === 'manual').length;
        const totalMappings = autoCount + manualCount;

        return {
            mappedPercent,
            autoCount,
            manualCount,
            totalActiveKpis,
            mappedCount,
            unmappedCount,
            totalMappings
        };
    };

    toggleFilterPanel = () => {
        this.setState(prevState => ({ showFiltersPanel: !prevState.showFiltersPanel }));
    };

    handleBusinessFilterToggle = (bizIdOrIds, checked) => {
        const nextSet = new Set(this.state.filterBusinesses);
        if (Array.isArray(bizIdOrIds)) {
            bizIdOrIds.forEach(id => {
                if (checked) nextSet.add(id);
                else nextSet.delete(id);
            });
        } else if (checked) {
            nextSet.add(bizIdOrIds);
        } else {
            nextSet.delete(bizIdOrIds);
        }
        this.setState({ filterBusinesses: nextSet, filterFunctionId: 'all' });
    };

    clearAllFilters = () => {
        this.setState({
            filterBusinesses: new Set(),
            filterFunctionId: 'all',
            filterStatus: 'all',
            searchVal: '',
            selectedNode: { type: null, id: null }
        });
    };

    handleToggleEnable = (mappingIdOrIds) => {
        const ids = Array.isArray(mappingIdOrIds) ? mappingIdOrIds : [mappingIdOrIds];
        if (!ids.length) return;
        const idSet = new Set(ids);
        const indexes = this.getIndexes();
        const group = this.state.mappings.filter(m => idSet.has(m.id));
        if (!group.length) return;
        const ctx = indexes.resolveMappingContext(group[0]);
        if (!ctx || !ctx.effectivelyActive) return;

        const allEnabled = group.every(m => m.enabled);
        const nextMappings = this.state.mappings.map(m =>
            idSet.has(m.id) ? { ...m, enabled: !allEnabled } : m
        );
        this.persistState({ mappings: nextMappings });
    };

    handleDeleteMapping = (mappingIdOrIds) => {
        const ids = Array.isArray(mappingIdOrIds) ? mappingIdOrIds : [mappingIdOrIds];
        if (!ids.length) return;
        this.setState({
            showDeleteModal: true,
            deleteMappingIds: [...ids]
        });
    };

    handleBulkDelete = (mappingIds) => {
        if (!mappingIds || mappingIds.length === 0) return;
        this.setState({
            showDeleteModal: true,
            deleteMappingIds: [...mappingIds]
        });
    };

    handleBulkEnable = (mappingIds) => {
        if (!mappingIds || mappingIds.length === 0) return;
        const idSet = new Set(mappingIds);
        const nextMappings = this.state.mappings.map(m =>
            idSet.has(m.id) ? { ...m, enabled: true } : m
        );
        this.persistState({ mappings: nextMappings });
    };

    handleBulkDisable = (mappingIds) => {
        if (!mappingIds || mappingIds.length === 0) return;
        const idSet = new Set(mappingIds);
        const nextMappings = this.state.mappings.map(m =>
            idSet.has(m.id) ? { ...m, enabled: false } : m
        );
        this.persistState({ mappings: nextMappings });
    };

    handleDeleteClose = () => {
        this.setState({
            showDeleteModal: false,
            deleteMappingIds: []
        });
    };

    handleConfirmDelete = () => {
        const { deleteMappingIds } = this.state;
        if (!deleteMappingIds.length) return;
        const idSet = new Set(deleteMappingIds);
        const nextMappings = this.state.mappings.filter(m => !idSet.has(m.id));
        this.persistState(
            { mappings: nextMappings },
            { showDeleteModal: false, deleteMappingIds: [] }
        );
    };

    handleEditMapping = (mapping) => {
        this.setState({
            editingMapping: mapping,
            modalOpen: true
        });
    };

    handleSaveMapping = (payload) => {
        const mappingsArray = Array.isArray(payload) ? payload : (payload.mappings || []);
        const formulaUpdate = Array.isArray(payload) ? null : payload.formulaUpdate;
        const replaceKpiId = Array.isArray(payload) ? null : (payload.replaceKpiId || null);
        const replaceMappingId = Array.isArray(payload)
            ? null
            : (replaceKpiId
                ? null
                : (payload.replaceMappingId || (this.state.editingMapping && this.state.editingMapping.id) || null));

        this.setState(prevState => {
            let nextMappings = upsertMappings(prevState.mappings, mappingsArray, { replaceMappingId, replaceKpiId });
            let nextAssets = [...prevState.assets];
            let nextKpis = prevState.kpis;

            mappingsArray.forEach(savedMapping => {
                if (savedMapping.asset && !nextAssets.some(a => a.id === savedMapping.asset.id)) {
                    nextAssets.push(savedMapping.asset);
                }
            });

            if (formulaUpdate && formulaUpdate.id) {
                nextKpis = prevState.kpis.map(k =>
                    k.id === formulaUpdate.id
                        ? { ...k, formula: formulaUpdate.formula }
                        : k
                );
            }

            const next = mergeConfigureState(prevState, {
                mappings: nextMappings,
                assets: nextAssets,
                kpis: nextKpis
            });
            saveConfigureState(next);

            return {
                businesses: next.businesses,
                functions: next.functions,
                kpis: next.kpis,
                models: next.models,
                assets: next.assets,
                mappings: next.mappings,
                modalOpen: false,
                editingMapping: null
            };
        });
    };

    matchesSearch = (mapping, indexes, searchVal) => {
        const q = (searchVal || '').trim().toLowerCase();
        if (!q) return true;
        const ctx = indexes.resolveMappingContext(mapping);
        if (!ctx) return false;
        const asset = ctx.asset;
        const haystack = [
            ctx.item && ctx.item.name,
            ctx.biz && ctx.biz.name,
            ctx.fn && ctx.fn.name,
            ctx.kpi && ctx.kpi.formula,
            asset && asset.datasource,
            asset && asset.dataset,
            asset && asset.attribute,
            mapping.status
        ].filter(Boolean).join(' ').toLowerCase();
        return haystack.includes(q);
    };

    getFilteredMappings = () => {
        const {
            mappings,
            searchVal,
            filterBusinesses,
            filterFunctionId,
            filterStatus
        } = this.state;
        const indexes = this.getIndexes();

        return mappings.filter(m => {
            const ctx = indexes.resolveMappingContext(m);
            // KPI-only domain mapping; drop unresolved / model-only stubs
            if (!ctx || !ctx.kpi) return false;
            if (!ctx.effectivelyActive) return false;

            const { biz, fn } = ctx;
            if (filterBusinesses.size > 0 && biz && !filterBusinesses.has(biz.id)) return false;
            if (filterFunctionId !== 'all' && fn && fn.id !== filterFunctionId) return false;
            if (filterStatus !== 'all' && m.status !== filterStatus) return false;
            if (!this.matchesSearch(m, indexes, searchVal)) return false;
            return true;
        });
    };

    render() {
        const {
            businesses,
            functions,
            kpis,
            models,
            assets,
            searchVal,
            selectedNode,
            modalOpen,
            editingMapping,
            showFiltersPanel,
            filterBusinesses,
            filterFunctionId,
            filterStatus,
            rightHeight
        } = this.state;

        const indexes = this.getIndexes();
        const treeStyle = (rightHeight && rightHeight > 992)
            ? { height: `${rightHeight}px` }
            : { maxHeight: '992px' };

        const { mappedPercent, autoCount, manualCount } = this.getMetrics();
        const activeBusinesses = businesses.filter(indexes.isBizEff);
        const scopedFunctions = filterBusinesses.size === 0
            ? functions.filter(indexes.isFnEff)
            : functions.filter(f => filterBusinesses.has(f.businessId) && indexes.isFnEff(f));

        const activeMappings = this.getFilteredMappings();
        const deleteCount = this.state.deleteMappingIds.length;

        return (
            <div className="portlet pb-0">
                <div className="portlet-title tw-flex tw-items-center tw-justify-between after:!tw-content-none after:!tw-hidden before:!tw-content-none before:!tw-hidden">
                    <div className="caption">
                        <span className="text-uppercase font-dark">Data Assets Mapping</span>
                        <small className='tw-text-sm tw-text-[#535862] tw-block'>Map each KPI to the datasource, table & attribute that powers it — overrides are <strong>Manual</strong>.</small>
                    </div>
                    <div className="actions !tw-flex tw-items-center tw-gap-2 tw-relative">
                        <div className="tw-flex tw-items-center tw-justify-center tw-px-3 tw-py-1 tw-bg-[#EFF2FE] dark:tw-bg-white/5 tw-border tw-border-solid tw-border-[#C7D2FE] dark:tw-border-white/10 tw-text-xs tw-font-semibold tw-text-[#6366F1] dark:tw-text-[#818CF8] tw-rounded-full">
                            {mappedPercent}% mapped
                        </div>
                        <span className="tw-text-[#D5D7DA] dark:tw-text-white/20 tw-text-xs">·</span>
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[#414651] dark:tw-text-[#7B7B86]">
                            <Check size={12} className='tw-mr-1' />
                            <span>{autoCount} auto</span>
                        </span>
                        <span className="tw-text-[#D5D7DA] dark:tw-text-white/20 tw-text-xs">·</span>
                        <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-[#414651] dark:tw-text-[#7B7B86]">
                            <Pencil size={12} className='tw-mr-1' />
                            <span>{manualCount} manual</span>
                        </span>
                        <button
                            type="button"
                            ref={this.infoIconRef}
                            onClick={() => this.setState(prev => ({ showDetailedMetrics: !prev.showDetailedMetrics }))}
                            className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-border-0 tw-text-slate-400 hover:tw-text-slate-600 dark:hover:tw-text-white tw-bg-transparent hover:tw-bg-slate-100 dark:hover:tw-bg-white/10 tw-cursor-pointer tw-p-0 tw-transition-colors"
                        >
                            <Info size={12} />
                        </button>

                        {/* Detailed Metrics Popup */}
                        {this.state.showDetailedMetrics && (
                            <div 
                                ref={this.infoPopupRef}
                                className="tw-absolute tw-right-0 tw-top-[calc(100%+8px)] tw-z-[100] tw-w-[280px] tw-bg-white dark:tw-bg-[#1E1E2D] tw-border tw-border-solid tw-border-[#E4E7EC] dark:tw-border-white/10 tw-rounded-xl tw-shadow-[0px_12px_40px_rgba(0,0,0,0.12)] tw-p-4 tw-text-left"
                            >
                                <h4 className="tw-text-sm tw-font-bold tw-text-gray-900 dark:tw-text-white tw-mb-3 tw-mt-0">
                                    Mapping calculations
                                </h4>
                                <div className="tw-border-0 tw-border-b tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-2"></div>
                                
                                <div className="tw-flex tw-items-center tw-justify-between">
                                    <span className="tw-text-[10px] tw-font-bold tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-uppercase tw-tracking-wider">
                                        Coverage · % mapped
                                    </span>
                                    <span className="tw-text-sm tw-font-bold tw-text-[#6366F1] dark:tw-text-[#818CF8]">
                                        {mappedPercent}%
                                    </span>
                                </div>

                                {(() => {
                                    const metrics = this.getMetrics();
                                    return (
                                        <>
                                            <div className="tw-bg-[#EFF2FE] dark:tw-bg-white/5 tw-text-[#6366F1] dark:tw-text-[#818CF8] tw-text-[11px] tw-font-semibold tw-rounded-lg tw-px-2 tw-py-1.5 tw-text-center tw-my-3">
                                                {metrics.mappedCount} ÷ {metrics.totalActiveKpis} active KPIs × 100
                                            </div>

                                            <div className="tw-space-y-1.5">
                                                <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                    <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">Active KPIs</span>
                                                    <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.totalActiveKpis}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                    <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">With an enabled mapping</span>
                                                    <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.mappedCount}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                    <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">Still unmapped</span>
                                                    <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.unmappedCount}</span>
                                                </div>
                                            </div>

                                            <div className="tw-border-0 tw-border-t tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-3"></div>

                                            <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
                                                <span className="tw-text-[10px] tw-font-bold tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-uppercase tw-tracking-wider">
                                                    Mappings by source
                                                </span>
                                                <span className="tw-text-xs tw-font-bold tw-text-gray-900 dark:tw-text-white">
                                                    {metrics.totalMappings} total
                                                </span>
                                            </div>

                                            <div className="tw-space-y-1.5">
                                                <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">
                                                        <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#10B981]"></span>
                                                        Auto-mapped by Qualdo
                                                    </span>
                                                    <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.autoCount}</span>
                                                </div>
                                                <div className="tw-flex tw-items-center tw-justify-between tw-text-xs">
                                                    <span className="tw-flex tw-items-center tw-gap-2 tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-font-medium">
                                                        <span className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-[#6366F1]"></span>
                                                        Manual overrides
                                                    </span>
                                                    <span className="tw-font-bold tw-text-gray-900 dark:tw-text-white">{metrics.manualCount}</span>
                                                </div>
                                            </div>

                                            <div className="tw-flex tw-h-1.5 tw-w-full tw-rounded-full tw-overflow-hidden tw-bg-gray-100 dark:tw-bg-white/10 tw-my-3">
                                                <div className="tw-bg-[#10B981]" style={{ width: `${metrics.totalMappings > 0 ? (metrics.autoCount / metrics.totalMappings) * 100 : 0}%` }} />
                                                <div className="tw-bg-[#6366F1]" style={{ width: `${metrics.totalMappings > 0 ? (metrics.manualCount / metrics.totalMappings) * 100 : 0}%` }} />
                                            </div>
                                        </>
                                    );
                                })()}

                                <div className="tw-border-0 tw-border-t tw-border-solid tw-border-[#F3F4F6] dark:tw-border-white/10 tw-my-3"></div>

                                <p className="tw-text-[10px] tw-text-[#717680] dark:tw-text-[#B2B5BE] tw-leading-relaxed tw-m-0">
                                    Inactive KPIs are excluded from coverage. A KPI can have several mappings, so Auto + Manual = total mappings, not KPI count.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                <div className='portlet-body !tw-p-0 tw-relative !tw-overflow-visible'>
                    <WorkspaceHeader
                        searchVal={searchVal}
                        onSearchChange={(val) => this.setState({ searchVal: val })}
                        onAddMappingClick={() => this.setState({ modalOpen: true, editingMapping: null })}
                        mappedPercent={mappedPercent}
                        autoCount={autoCount}
                        manualCount={manualCount}
                        toggleFilterPanel={this.toggleFilterPanel}
                        showFiltersPanel={showFiltersPanel}
                        activeBusinesses={activeBusinesses}
                        filterBusinesses={filterBusinesses}
                        onBusinessFilterToggle={this.handleBusinessFilterToggle}
                        scopedFunctions={scopedFunctions}
                        filterFunctionId={filterFunctionId}
                        onFunctionFilterChange={(val) => this.setState({ filterFunctionId: val })}
                        filterStatus={filterStatus}
                        onStatusFilterChange={(val) => this.setState({ filterStatus: val })}
                        clearAllFilters={this.clearAllFilters}
                        matchingCount={activeMappings.length}
                    />

                    <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-stretch">
                        <div
                            className="tw-w-full lg:tw-w-[320px] tw-flex-shrink-0 tw-flex tw-flex-col"
                            style={treeStyle}
                        >
                            <LeftTreePanel
                                businesses={businesses}
                                functions={functions}
                                kpis={kpis}
                                models={[]}
                                mappings={activeMappings}
                                selectedNode={selectedNode}
                                onSelectNode={(type, id) => this.setState({ selectedNode: { type, id } })}
                                searchQuery={searchVal}
                                showModels={false}
                                indexes={indexes}
                                onlyActive
                            />
                        </div>

                        <div ref={this.rightPanelRef} className="tw-flex-1 tw-min-w-0 tw-flex tw-flex-col">
                            <RightTableSection
                                businesses={businesses}
                                functions={functions}
                                kpis={kpis}
                                models={models}
                                assets={assets}
                                mappings={activeMappings}
                                selectedNode={selectedNode}
                                indexes={indexes}
                                onToggleEnable={this.handleToggleEnable}
                                onDeleteMapping={this.handleDeleteMapping}
                                onBulkDelete={this.handleBulkDelete}
                                onBulkEnable={this.handleBulkEnable}
                                onBulkDisable={this.handleBulkDisable}
                                onEditMapping={this.handleEditMapping}
                            />
                        </div>
                    </div>

                    <AddMappingModal
                        show={modalOpen}
                        onClose={() => this.setState({ modalOpen: false, editingMapping: null })}
                        onSave={this.handleSaveMapping}
                        editingMapping={editingMapping}
                        businesses={activeBusinesses}
                        functions={functions.filter(indexes.isFnEff)}
                        kpis={kpis.filter(indexes.isKpiEff)}
                        assets={assets}
                        mappings={this.state.mappings}
                    />

                    <DeleteWarningModal
                        delete_show={this.state.showDeleteModal}
                        deleteClose={this.handleDeleteClose}
                        handleDeleteFunction={this.handleConfirmDelete}
                        component={deleteCount > 1 ? `${deleteCount} KPI Mappings` : 'KPI Mapping'}
                        modalTitle={deleteCount > 1 ? 'KPI Mappings' : 'KPI Mapping'}
                    />
                </div>
            </div>
        );
    }
}

export default DomainMappingWorkspace;
